import React, {memo} from 'react';
import cn from 'classnames';
import {useLocalizer} from '@/common/services/localizer';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {WeatherForecastLocation} from '@/app/interfaces/weather';
import {RouteName} from '@/app/constants/routing';
import {ForecastDaysMode} from '@/app/constants/forecast';
import {RouterLink} from '@/common/components/RouterLink';
import {AdBanner} from '@/app/components/common/advertising';
import {PageHeader} from '@/app/components/desktop/common/PageHeader';
import {TextLink} from '@/common/components/TextLink';
import {GeolocationMismatchConfirm, useGeolocationMismatchCheck} from '@/app/components/common/GeolocationMismatchConfirm';
import styles from './WeatherHeader.css';

interface WeatherHeaderProps {
	location: WeatherForecastLocation;
	selectedDaysMode: ForecastDaysMode;
	isMainPage: boolean;
}

export const WeatherHeader = memo<WeatherHeaderProps>(props => {
	const {location, selectedDaysMode, isMainPage} = props;

	const t = useLocalizer();
	const urlBuilder = useUrlBuilder();

	const {geolocationMismatched, handleMismatchSkip, handleMismatchRefuse} = useGeolocationMismatchCheck({
		isMainPage,
		currentLocationId: location.id,
	});

	return (
		<PageHeader
			title={
				<div className={styles.leading}>
					<p className={styles.title}>
						{t('weather')} <span className={styles.locationName}>{location.titleLc}</span>
					</p>
					{geolocationMismatched && (
						<GeolocationMismatchConfirm
							className={styles.geolocationMismatchPopup}
							onClose={handleMismatchSkip}
							onRefused={handleMismatchRefuse}
						/>
					)}
				</div>
			}
			description={location.description}
			trailing={<AdBanner interfaceName='desktop' zoneName='header' />}>
			<div className={styles.navigation}>
				{Object.values(ForecastDaysMode).map((option, index) => (
					<RouterLink
						key={option || 'default'}
						component={TextLink}
						className={cn(styles.navigationLink, option === selectedDaysMode && styles.isSelected)}
						url={urlBuilder(RouteName.weather, {location, daysMode: option})}
						tabIndex={option === selectedDaysMode && styles.isSelected ? -1 : undefined}
						title={t(index === 0 ? 'weatherDailyWeek' : 'weatherDaily10Days', {
							locationIn: location.titleLc,
						})}
						role='menuitem'>
						{t(`dayModes.${option || '7days'}`)}
					</RouterLink>
				))}
			</div>
		</PageHeader>
	);
});

WeatherHeader.displayName = 'WeatherHeader';
